import { useEffect } from "react";
import {
  Link,
  useActionData,
  useLoaderData,
  useParams,
  useSearchParams,
} from "@remix-run/react";
import { ActionFunctionArgs, json, LoaderFunctionArgs,  MetaFunction } from "@remix-run/node";
import { ProductCard } from "~/components/ProductCard";
import { useStoreContext } from "~/hooks/use-store";
import {
  getSupabaseWithSessionHeaders,
  supabaseAdmin,
} from "~/lib/supabase_server";
import { isProductMediaImageFileName } from "~/lib/supabase";
import { ProductDetail } from "~/components/ProductDetail";
import { useRecentlyViewed } from "~/hooks/useRecentlyViewed";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { StoreEmptyProducts } from "~/components/store/StoreEmptyProducts";
import { PUBLIC_STORE_PROFILES } from "~/lib/public_store_profiles";
import { isPublicStoreSlugParam } from "~/lib/helpers/storehelpers";
import { serverPublicOrigin } from "~/lib/serverPublicOrigin";

export async function loader({ params, request }: LoaderFunctionArgs) {
  const { headers, supabase, session } = await getSupabaseWithSessionHeaders({
    request,
  });
  const publicOrigin = serverPublicOrigin(request);
  const storeName = params.id || "";
  if (!storeName) {
    throw new Response("Store name is required", {
      status: 400,
      headers: headers,
    });
  }

  if (!isPublicStoreSlugParam(storeName)) {
    throw new Response("Store not found", {
      status: 404,
      headers: headers,
    });
  }

  // Fetch store data (public profile view — ACTIVE stores only)
  const { data: store, error: storeError } = await supabase
    .from(PUBLIC_STORE_PROFILES)
    .select("*")
    .ilike("slug", storeName)
    .single();

  if (storeError || !store) {
    throw new Response("Store not found", {
      status: 404,
      headers: headers,
    });
  }

  // Check for product param in URL
  const url = new URL(request.url);
  const productId = url.searchParams.get("product");

  if (productId) {
    // Fetch product data (only active products with a valid price)
    const { data: product, error: productError } = await supabaseAdmin
      .from("Products")
      .select("*")
      .eq("id", productId)
      .eq("store_id", store.id)
      .eq("status", "ACTIVE")
      .single();

    if (productError || !product) {
      return json({ product: null, productNotFound: true, store, publicOrigin }, { headers });
    }

    // Fetch product reviews with user information
    const { data: reviews, error: reviewsError } = await supabase
      .from("ProductReviews")
      .select("*")
      .eq("product_id", productId)
      .order("created_at", { ascending: false });

    const reviewsWithUsers: Array<{
      id: string;
      rating: number;
      comment: string | null;
      created_at: string;
      user_id: string;
      reviewerName: string;
      reviewerInitials: string;
    }> = [];

    if (reviews && !reviewsError && reviews.length > 0) {
      const reviewerIds = [...new Set(reviews.map((r) => r.user_id))];
      const { data: users } = await supabase
        .from("Users")
        .select("id, first_name, last_name")
        .in("id", reviewerIds);

      const userMap = new Map(
        (users ?? []).map((u) => [u.id, u]),
      );

      for (const review of reviews) {
        const userData = userMap.get(review.user_id);
        if (!userData) continue;
        const initials = `${userData.first_name?.charAt(0) ?? ""}${userData.last_name?.charAt(0) ?? ""}`.toUpperCase();
        reviewsWithUsers.push({
          ...review,
          reviewerName:
            `${userData.first_name ?? ""} ${userData.last_name ?? ""}`.trim() || "Anonymous",
          reviewerInitials: initials || "A",
        });
      }
    }

    // Fetch "You May Also Like" recommendations (other products from same store)
    const { data: recommendations, error: recError } = await supabaseAdmin
      .from("Products")
      .select("*")
      .eq("store_id", store.id)
      .eq("status", "ACTIVE")
      .neq("id", productId)
      .limit(10);

    // Pre-process image URLs for recommendations
    const recommendationsWithImages = (recommendations || []).map(
      (recProduct) => {
        let mainImageUrl: string | undefined;
        if (
          recProduct.media_urls &&
          typeof recProduct.media_urls === "object"
        ) {
          const firstImage = Object.keys(recProduct.media_urls).find((image) =>
            isProductMediaImageFileName(image)
          );
          if (firstImage && recProduct.media_urls[firstImage]) {
            mainImageUrl = recProduct.media_urls[firstImage];
          }
        }
        return {
          ...recProduct,
          mainImageUrl: mainImageUrl || "/images/placeholder.svg",
        };
      },
    );

    // Get image URLs from media_urls for the main product
    const imageUrls: string[] = [];
    if (product.media_urls && typeof product.media_urls === "object") {
      const mediaKeys = Object.keys(product.media_urls);
      for (const key of mediaKeys) {
        if (isProductMediaImageFileName(key)) {
          const url = product.media_urls[key];
          if (url) imageUrls.push(url);
        }
      }
    }

    // If no media, add placeholder
    if (imageUrls.length === 0) {
      imageUrls.push("/images/placeholder.svg");
    }

    // Calculate average rating
    const averageRating = reviewsWithUsers.length > 0
      ? reviewsWithUsers.reduce((sum, r) => sum + r.rating, 0) /
        reviewsWithUsers.length
      : product.average_rating || 0;

    return json(
      {
        product,
        store,
        reviews: reviewsWithUsers,
        recommendations: recommendationsWithImages,
        imageUrls,
        averageRating,
        productNotFound: false,
        userId: session?.user?.id || null,
        publicOrigin,
      },
      { headers },
    );
  }

  return json({ product: null, productNotFound: false, store, publicOrigin }, { headers });
}

export const meta: MetaFunction<typeof loader> = ({
  data,
  matches,
  location,
}) => {
  const parentMeta = matches .find((match) => match.id === "routes/store.$id")?.meta ?? [];

  const productId = new URLSearchParams(location.search).get("product");

  if (!productId || !data?.product) {
    return parentMeta;
  }

  return [
    ...parentMeta,
    {
      tagName: "link",
      rel: "canonical",
      href: `${data.publicOrigin}/product/${productId}`,
    },
  ];
};

export async function action({ request, params }: ActionFunctionArgs) {
  const { supabase } = await getSupabaseWithSessionHeaders({ request });
  const formData = await request.formData();
  const action = formData.get("action");

  if (action === "addReview") {
    const rating = Number(formData.get("rating"));
    const title = (formData.get("title") as string) || null;
    const comment = formData.get("comment") as string;

    // Validate rating is a valid number between 1 and 5
    if (
      isNaN(rating) || !Number.isInteger(rating) || rating < 1 || rating > 5
    ) {
      return json({ error: "Rating must be a whole number between 1 and 5" }, {
        status: 400,
      });
    }

    // Get productId from URL search params
    const url = new URL(request.url);
    const productId = url.searchParams.get("product");

    if (!productId) {
      return json({ error: "Product ID is required" }, { status: 400 });
    }

    // Get current user
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      return json(
        { error: "You must be logged in to write a review" },
        { status: 401 },
      );
    }

    // Verify the user has purchased this product
    const { data: userOrders } = await supabase
      .from("Orders")
      .select("id")
      .eq("customer_id", user.id);

    const orderIds = (userOrders ?? []).map((o) => o.id);

    let hasPurchased = false;
    if (orderIds.length > 0) {
      const { count: purchaseCount } = await supabase
        .from("OrderItems")
        .select("id", { count: "exact", head: true })
        .eq("product_id", productId)
        .in("order_id", orderIds);

      hasPurchased = (purchaseCount ?? 0) > 0;
    }

    if (!hasPurchased) {
      return json(
        { error: "A purchase must be made before review." },
        { status: 403 },
      );
    }

    const { count: reviewCount } = await supabase
      .from("ProductReviews")
      .select("id", { count: "exact", head: true })
      .eq("product_id", productId)
      .eq("user_id", user.id);

    if (reviewCount && reviewCount > 0) {
      return json(
        { error: "You have already reviewed this product" },
        { status: 409 },
      );
    }

    // Insert review
    const { error: reviewError } = await supabase
      .from("ProductReviews")
      .insert({
        product_id: productId,
        user_id: user.id,
        rating,
        title,
        comment: comment || null,
      });

    if (reviewError) {
      console.error("Error adding review:", reviewError);
      return json({ error: "Failed to add review" }, { status: 500 });
    }

    return json({ success: true });
  }

  return json({ error: "Invalid action" }, { status: 400 });
}

export default function ProductsPage() {
  const loaderData = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { storeProducts, storeDetails } = useStoreContext();
  const { addToRecentlyViewed } = useRecentlyViewed();

  const productId = searchParams.get("product");

  // Track product view when a product detail is displayed
  useEffect(() => {
    if (productId && loaderData.product) {
      const product = loaderData.product;
      const imageUrls = "imageUrls" in loaderData
        ? loaderData.imageUrls || []
        : [];
      addToRecentlyViewed({
        id: product.id,
        name: product.name,
        price: product.price,
        markdown: product.markdown,
        mainImageUrl: imageUrls[0] || product.main_image || null,
        storeId: loaderData.store?.id || product.store_id,
        storeName: loaderData.store?.name || null,
      });
    }
  }, [productId]);

  // If product param exists and we have product data, show product detail
  if (productId && loaderData.product) {
    return (
      <div className="flex flex-1 flex-col">
        {/* Breadcrumb */}
        <div className="container px-4 md:px-6 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={`/store/${params.id}/products`}>Products</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {loaderData.product.name || "Product"}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <ProductDetail
          product={loaderData.product}
          store={loaderData.store}
          reviews={"reviews" in loaderData ? loaderData.reviews || [] : []}
          recommendations={"recommendations" in loaderData
            ? loaderData.recommendations || []
            : []}
          imageUrls={"imageUrls" in loaderData
            ? loaderData.imageUrls || []
            : []}
          averageRating={"averageRating" in loaderData
            ? loaderData.averageRating || 0
            : 0}
          actionData={actionData}
          userId={"userId" in loaderData ? loaderData.userId : null}
        />
      </div>
    );
  }

  // If product param exists but product not found
  if (productId && loaderData.productNotFound) {
    return (
      <div className="flex flex-1 flex-col">
        {/* Breadcrumb */}
        <div className="container px-4 md:px-6 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={`/store/${params.id}/products`}>Products</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Product Not Found</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="container flex-1 px-4 md:px-6 py-12">
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4">
              Product does not exist
            </h2>
            <p className="text-gray-600">
              The product you're looking for could not be found.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Default: show products list
  return (
    <div className="flex flex-1 flex-col">
      <section className="flex-1 w-full py-12 md:py-24 lg:py-12">
        <div className="container px-4 md:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-4 sm:gap-5 lg:gap-6">
            {storeProducts.length > 0
              ? (
                storeProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    productId={product.id}
                    productDetails={product}
                    storeName={storeDetails.name}
                    size="promo"
                    fluid
                  />
                ))
              )
              : (
                <div className="col-span-full">
                  <StoreEmptyProducts storeName={storeDetails.name} />
                </div>
              )}
          </div>
        </div>
      </section>
    </div>
  );
}
